import React from "react";
import { Stack, Typography, Divider, Box, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormCurrencyField from "../components/common/FormCurrencyField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createCustomer,
  updateCustomer,
} from "../redux/actions/customerActions";
import { cleanFormData } from "../helpers/formHelpers";
import { customerActions } from "../redux/slices/customer";

import { CustomerRequestDto } from "../shared/dtos/customer/CustomerRequestDto";
import { CustomerResponseDto } from "../shared/dtos/customer/CustomerResponseDto";

const customerSchema = Yup.object().shape({
  name: Yup.string().required("Customer/Business name is required"),
  primaryContact: Yup.string().required("Primary contact is required"),
  secondaryContact: Yup.string().nullable(),
  email: Yup.string().email("Invalid email").nullable(),
  taxId: Yup.string().nullable(),
  creditLimit: Yup.number()
    .min(0, "Cannot be negative")
    .required("Credit limit is required"),
  address: Yup.object().shape({
    streetLine1: Yup.string().required("Street address is required"),
    streetLine2: Yup.string().nullable(),
    city: Yup.string().required("City is required"),
    stateOrProvince: Yup.string().nullable(),
    postalCode: Yup.string().nullable(),
    country: Yup.string().required("Country is required"),
  }),
});

interface CreateOrUpdateCustomerFormProps {
  customer?: CustomerResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateCustomerForm: React.FC<CreateOrUpdateCustomerFormProps> = ({
  customer = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<CustomerRequestDto> = {
    name: customer?.name || "",
    primaryContact: customer?.primaryContact || "",
    secondaryContact: customer?.secondaryContact || "",
    email: customer?.email || "",
    taxId: customer?.taxId || "",
    creditLimit: customer?.creditLimit || 0,
    address: {
      streetLine1: customer?.address?.streetLine1 || "",
      streetLine2: customer?.address?.streetLine2 || "",
      city: customer?.address?.city || "",
      stateOrProvince: customer?.address?.stateOrProvince || "",
      postalCode: customer?.address?.postalCode || "",
      country: customer?.address?.country || "Kenya",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? customerActions.setErrorUpdate(null)
              : customerActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={customerSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(
            values,
          ) as unknown as CustomerRequestDto;
          if (isEditing && customer) {
            dispatch(updateCustomer({ id: customer.id, values: payload }));
          } else {
            dispatch(createCustomer(payload));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<Partial<CustomerRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Basic Information
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 1,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  }}
                >
                  <FormTextField name="name" label="Business/Customer Name" />
                  <FormTextField name="taxId" label="Tax ID / PIN" />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="primaryContact"
                    label="Primary Contact"
                  />
                  <FormTextField
                    name="secondaryContact"
                    label="Secondary Contact"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField name="email" label="Email Address" />
                  <FormCurrencyField name="creditLimit" label="Credit Limit" />
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.5),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Physical Address
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 1,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="address.streetLine1"
                    label="Street Line 1"
                  />
                  <FormTextField
                    name="address.streetLine2"
                    label="Street Line 2"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormTextField name="address.city" label="City" />
                  <FormTextField
                    name="address.stateOrProvince"
                    label="State/Province"
                  />
                  <FormTextField name="address.country" label="Country" />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr" },
                  }}
                >
                  <FormTextField
                    name="address.postalCode"
                    label="Postal Code"
                  />
                </Box>
              </Box>

              <SubmitButton
                title={isEditing ? "Update Customer" : "Register Customer"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateCustomerForm;
